import React, { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from "react";
import { useCompanyInfo } from "../redux/company";
import { ModalError, useSetError } from "../redux/modals";
import { awaitJSON, MetaLambdaFetch } from "../utils/fetch-utils";
import {
  DataItem,
  FullPageSpinner,
  InfoTooltip,
  Page,
  SnapshotChart,
  DatePicker,
  IconToggleButton,
  DownloadDropdown,
} from "../Components";
import WidgetContainer from "../Components/WidgetContainer";
import "./SignalDashboard.scss";
import ChartContainer from "../Components/ChartContainer";
import LineChart from "../Components/Charts/LineChart";
import { MdOutlineTableRows, MdOutlineWarningAmber, MdShowChart } from "react-icons/md";
import { DateRange } from "../utils/types";
import * as Dfns from "date-fns/fp";
import { numberFormatter } from "../MMM/MMMUtils";
import MetricsTable from "../SingleChannel/MetricsTable/MetricsTable";
import { downloadPNG, exportToExcel } from "../utils/download-utils";

const TABLE_HEIGHT = 320;

interface EMQScores {
  date: string;
  [eventName: string]: number | string;
}

const SignalDashboard = (): JSX.Element => {
  const { cid: company } = useCompanyInfo();
  const [chartTypeView, setChartTypeView] = useState<string>("showGraph");
  const setError = useSetError();
  const [emqScores, setEmqScores] = useState<EMQScores[]>();
  const [events, setEvents] = useState<{ dataKey: string; name: string }[]>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [dateRange, setDateRange] = useState<DateRange>({
    start: Dfns.format("yyyy-MM-dd", Dfns.subDays(30, new Date())),
    end: Dfns.format("yyyy-MM-dd", new Date()),
  });

  useEffect(() => {
    (async () => {
      if (!emqScores) {
        try {
          const res = await MetaLambdaFetch("/getEMQScores", {
            params: {
              company,
            },
          });

          const parsedRes = await awaitJSON<EMQScores[]>(res);
          setEmqScores(parsedRes);
          let eventNames: { dataKey: string; name: string }[] = [];
          Object.keys(parsedRes[0]).forEach(key => {
            if (key !== "date") {
              eventNames.push({ dataKey: key, name: key });
            }
          });
          setEvents(eventNames);
        } catch (e) {
          setError({
            message: (e as ModalError).message,
            reportError: (e as ModalError).reportError,
          });
        }
      }
    })();
  }, [company, setError, emqScores]);

  const filteredEmqScores = useMemo(() => {
    if (!emqScores || !dateRange) {
      return [];
    }

    return emqScores.filter(({ date }) => {
      const d = Dfns.parseISO(date);
      return d >= Dfns.parseISO(dateRange.start) && d <= Dfns.parseISO(dateRange.end);
    });
  }, [emqScores, dateRange]);

  const [avgEmqScores, domain] = useMemo(() => {
    if (!filteredEmqScores.length) {
      return [[], [0, 10] as [number, number]];
    }

    let minVal = 10;
    let maxVal = 0;
    const sumMap: { [event: string]: { total: number; count: number } } = {};
    filteredEmqScores.forEach(item => {
      Object.entries(item).forEach(([key, value]) => {
        if (key !== "date" && typeof value === "number" && !Number.isNaN(value)) {
          if (!sumMap[key]) {
            sumMap[key] = { total: 0, count: 0 };
          }
          sumMap[key].total += value;
          sumMap[key].count += 1;

          if (value < minVal) {
            minVal = value;
          }
          if (value > maxVal) {
            maxVal = value;
          }
        }
      });
    });

    return [
      Object.entries(sumMap).map(([event, { total, count }]) => ({
        name: event,
        value: total / count,
      })),
      [minVal, maxVal] as [number, number],
    ];
  }, [filteredEmqScores]);

  useEffect(() => {
    if (emqScores?.length && avgEmqScores?.length) {
      setIsLoading(false);
    }
  }, [emqScores, avgEmqScores]);

  const containerRef = useRef(null);
  const [tableWidth, setTableWidth] = useState(500);
  const [rerenderTable, setRerenderTable] = useState(false);
  useLayoutEffect(() => {
    const newTableWidth = containerRef.current
      ? (containerRef.current as any).offsetWidth
      : undefined;
    if (newTableWidth && chartTypeView === "showTable") {
      setTableWidth((containerRef.current as any).offsetWidth - 5);
    }
    if (rerenderTable && chartTypeView === "showTable") {
      setRerenderTable(false);
    }
  }, [rerenderTable, chartTypeView]);

  const dimensionData = useMemo(() => {
    if (!events) {
      return [];
    }

    let output: { [key: string]: { label: string; value: string } }[] = [];
    events.forEach(event => {
      output.push({ Event: { label: event.name, value: event.dataKey } });
    });

    return output;
  }, [events]);

  const dataColumns = useMemo(() => {
    if (!filteredEmqScores || filteredEmqScores.length === 0) {
      return [];
    }

    // Get unique dates from the filtered data
    const uniqueDates = Array.from(new Set(filteredEmqScores.map(item => item.date))).sort();

    // Create column objects for each date
    return uniqueDates.map(date => ({
      id: date,
      dataVarName: "date",
      label: Dfns.format("yyyy-MM-dd", Dfns.parseISO(date)),
    }));
  }, [filteredEmqScores]);

  const tableData = useMemo(() => {
    if (!filteredEmqScores || !events || filteredEmqScores.length === 0) {
      return [] as any[];
    }

    // Transform data: each event becomes a row, each date becomes a column
    return events.map(event => {
      const rowData: any[] = [];

      // Add values for each date
      dataColumns.forEach(column => {
        const dateData = filteredEmqScores.find(item => item.date === column.id);
        const value = dateData ? dateData[event.dataKey] : null;

        rowData.push({
          value: value,
          label:
            value !== null && typeof value === "number" ? numberFormatter.format(value, 1) : "--",
        });
      });

      return rowData;
    });
  }, [filteredEmqScores, events, dataColumns]);

  const pngDownload = async () => {
    await downloadPNG(".signalDashboardChartContainer", "Signal Dashboard");
  };

  const excelDownload = useCallback(() => {
    exportToExcel(filteredEmqScores, `${company}_emq_scores`);
  }, [company, filteredEmqScores]);

  return (
    <Page app2Redesign title="Meta Signal Dashboard" pageType={"Meta Signal Dashboard"}>
      {isLoading ? (
        <FullPageSpinner />
      ) : (
        <div className="signalDashboardPage">
          <WidgetContainer
            header="Event Match Quality"
            subHeader={
              <>
                High quality event matching may improve ads attribution and performance. Note that
                this metric is in development by Meta.
                <InfoTooltip rightLabel="More info" size="sm">
                  Event Match Quality (EMQ) is a score (out of 10) that indicates how effective the
                  customer information sent from your server may be at matching event instances to a
                  Facebook account. It's calculated by looking at which customer information
                  parameters are received from your server via a Conversions API integration, the
                  quality of the information received, and the percent of event instances that are
                  matched to a Meta account.
                </InfoTooltip>
              </>
            }
            bottom={
              <div className="signalDashboardBottomWrapper">
                <MdOutlineWarningAmber style={{ fontSize: 22, marginRight: 14 }} />
                <div>EMQ is currently only available for web events.</div>
              </div>
            }
            rightActions={
              <DatePicker
                light={true}
                range={dateRange}
                onChange={setDateRange}
                maxDate={Dfns.format("yyyy-MM-dd", new Date())}
              />
            }
          >
            <div className="signalDashboardContainer">
              <SnapshotChart
                data={avgEmqScores as DataItem[]}
                customXAxisDomain={[0, 10]}
                title="Average EMQ Scores"
                valueFormatter={value => numberFormatter.format(value, 1)}
              ></SnapshotChart>
              <ChartContainer
                className="signalDashboardChartContainer"
                enableHoverDesign
                title="Historical EMQ Scores"
                rightActions={
                  <div className="signalDashboardRightActions">
                    <IconToggleButton
                      options={[
                        { key: "showGraph", icon: <MdShowChart />, label: "graph view" },
                        { key: "showTable", icon: <MdOutlineTableRows />, label: "table view" },
                      ]}
                      size="sm"
                      selectedOption={chartTypeView}
                      onChange={selected => setChartTypeView(selected)}
                    />
                    <DownloadDropdown
                      menuOptions={chartTypeView === "showGraph" ? ["PNG"] : ["XLSX"]}
                      size="sm"
                      onClickOptions={
                        chartTypeView === "showGraph" ? [pngDownload] : [excelDownload]
                      }
                    />
                  </div>
                }
              >
                {chartTypeView === "showGraph" && (
                  <LineChart
                    data={filteredEmqScores as any[]}
                    customLeftYAxisDomain={domain}
                    lineDataKeys={events || []}
                    xAxisDataKey={"date"}
                    useLeftYAxis={true}
                    tooltipShape="line"
                    customTooltipHeader={val => val}
                    yAxisTickFormatter={value => numberFormatter.format(value, 1)}
                  />
                )}
                {chartTypeView === "showTable" && (
                  <div ref={containerRef} style={{ width: "100%" }}>
                    <MetricsTable
                      tableData={tableData}
                      aggregateData={[]}
                      dataColumns={dataColumns}
                      dimensionColumns={[
                        {
                          id: "name",
                          dimensionVarName: "Event",
                          label: "Event",
                          dimensionTypeName: "Event",
                          divider: false,
                        },
                      ]}
                      dimensionData={dimensionData}
                      height={TABLE_HEIGHT}
                      width={tableWidth}
                    />
                  </div>
                )}
              </ChartContainer>
            </div>
          </WidgetContainer>
        </div>
      )}
    </Page>
  );
};

export default SignalDashboard;
